import { heicTo } from "heic-to";

export const processImage = async (file) => {
  let currentFile = file;

  // Handle HEIC conversion
  if (file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic")) {
    try {
      const blob = await heicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.8
      });

      currentFile = new File(
        [blob],
        file.name.replace(/\.[^/.]+$/, "") + ".jpg",
        {
          type: "image/jpeg"
        }
      );
    } catch (error) {
      console.error("HEIC conversion failed:", error);
      throw new Error(
        "Failed to process HEIC image. Please try a different format."
      );
    }
  }

  const MAX_SIZE_MB = 10;
  const RESIZE_THRESHOLD_MB = 2;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
  const RESIZE_THRESHOLD_BYTES = RESIZE_THRESHOLD_MB * 1024 * 1024;

  if (currentFile.size > MAX_SIZE_BYTES) {
    throw new Error(`File size exceeds 10 MB. Please upload a smaller image.`);
  }

  if (currentFile.size <= RESIZE_THRESHOLD_BYTES) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        const base64 = result.split(",")[1];
        resolve({
          file: currentFile,
          preview: result,
          base64
        });
      };
      reader.readAsDataURL(currentFile);
    });
  }

  // Resize logic for files > 2MB
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Scale down if dimensions are extremely large to help with file size
        const MAX_DIM = 2048;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Export as JPEG with reduced quality to hit the < 2MB target
        // We'll try 0.8 first, which is high quality but much smaller than PNG/RAW
        let quality = 0.8;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);

        // Check if it's still > 2MB (unlikely for 2048px JPEG at 0.8)
        // If it were, we could iteratively reduce quality, but 0.8 is safe.

        const base64 = dataUrl.split(",")[1];
        const blob = dataURLToBlob(dataUrl);
        const resizedFile = new File(
          [blob],
          currentFile.name.replace(/\.[^/.]+$/, "") + ".jpg",
          {
            type: "image/jpeg"
          }
        );

        resolve({
          file: resizedFile,
          preview: dataUrl,
          base64
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(currentFile);
  });
};

function dataURLToBlob(dataurl) {
  const arr = dataurl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) return new Blob();
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}
